import { useEffect, useState } from 'react';

/**
 * Live-updating elapsed time in ms since `active` last became true; null when
 * inactive. Ticks ~10×/sec so a timer label animates smoothly. Used by the "Ask"
 * buttons to show how long a query is taking.
 *
 * Kept lint-clean for the strict React rules: render is pure (no `Date.now()` /
 * ref reads), and `setState` runs only from async callbacks (rAF + interval),
 * never synchronously in the effect body.
 */
export function useLiveElapsed(active: boolean): number | null {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!active) return;
    const startedAt = Date.now();
    const tick = () => setElapsedMs(Date.now() - startedAt);
    const raf = requestAnimationFrame(tick); // paint ~0 promptly instead of waiting 100ms
    const id = setInterval(tick, 100);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, [active]);

  return active ? elapsedMs : null;
}
